import React, { useState } from "react";
import { FaCheckCircle, FaShoppingBag, FaCopy, FaCheck } from "react-icons/fa";
import toast from "react-hot-toast";
import { getOrderStatusLabel } from "../../TrackOrder/trackOrderConstants";

const OrderConfirmedHero = ({ order }) => {
  const [copied, setCopied] = useState(false);

  const orderNumber = order?.order_number || `KDF-${order?.order_id || "784920"}`;
  const placedDate = order?.placed_at || order?.created_at
    ? new Date(order.placed_at || order.created_at).toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "Just now";

  const handleCopyOrderId = () => {
    navigator.clipboard.writeText(orderNumber);
    setCopied(true);
    toast.success("Order ID copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-2xl border border-emerald-100 bg-white p-4 sm:p-7 shadow-sm">
      {/* ── Top row: icon+text left, order-ref right (stacks on mobile) ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* Left: success icon + heading */}
        <div className="flex items-center gap-3">
          <div className="relative flex h-14 w-14 sm:h-20 sm:w-20 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 border border-emerald-200">
            <FaShoppingBag className="h-7 w-7 sm:h-10 sm:w-10 text-[#046a38]" />
            <div className="absolute -bottom-1 -right-1 flex h-5 w-5 sm:h-6 sm:w-6 items-center justify-center rounded-full bg-[#046a38] text-white ring-2 ring-white">
              <FaCheckCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
            </div>
          </div>

          <div>
            <span className="inline-block rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] sm:text-xs font-bold text-[#046a38] mb-1">
              Order Placed Successfully
            </span>
            <h1 className="text-base sm:text-2xl font-black text-gray-900 leading-snug">
              Thank You for Your Order!
            </h1>
            <p className="text-[11px] sm:text-sm text-gray-500 mt-0.5 hidden sm:block">
              We have received your order and are preparing it with care.
            </p>
          </div>
        </div>

        {/* Right: Order Reference box */}
        <div className="flex items-center justify-between sm:flex-col sm:items-end gap-1 bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-100 sm:w-auto">
          <span className="text-[10px] sm:text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
            Order Reference
          </span>
          <div className="flex items-center gap-2">
            <span className="text-sm sm:text-base font-black text-[#046a38]">
              {orderNumber}
            </span>
            <button
              onClick={handleCopyOrderId}
              className="rounded p-1 text-gray-400 hover:text-[#046a38] transition"
              title="Copy Order ID"
            >
              {copied ? (
                <FaCheck className="h-3.5 w-3.5 text-[#046a38]" />
              ) : (
                <FaCopy className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
          <span className="text-[10px] text-gray-400">{placedDate}</span>
        </div>
      </div>

      {/* Order Status Banner */}
      <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600 bg-emerald-50/50 rounded-lg px-2.5 py-2">
        <div className="flex items-center gap-2">
          <span className="flex h-2 w-2 rounded-full bg-[#046a38] animate-pulse shrink-0" />
          <span className="font-semibold text-gray-800">Status:</span>
          <span className="font-bold text-[#046a38] uppercase">
            {getOrderStatusLabel(order?.order_status || order?.status || "CONFIRMED")}
          </span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
          {order?.delivery_type && (
            <>
              <span className="text-gray-500">Delivery:</span>
              <span className="font-bold text-gray-900">{order.delivery_type}</span>
            </>
          )}
          {(order?.delivery_slot_name || order?.slot_name) && (
            <span className="font-semibold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-200">
              {order.delivery_slot_name || order.slot_name}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmedHero;
